import { useState, useCallback } from 'react'
import { SubsessionOptions } from 'zoom/types/subsession'

type SubsessionOptionsState = {
    isAutoJoinSubsession: boolean
    isBackToMainSessionEnabled: boolean
    isTimerEnabled: boolean
    timerDuration: number
    isTimerAutoEnabled: boolean
    waitSeconds: number
    isAutoMoveBackToMainSession: boolean
    isSubsessionSelectionEnabled: boolean
}

type SubsessionOptionsActions = {
    setIsAutoJoinSubsession: React.Dispatch<React.SetStateAction<boolean>>
    setIsBackToMainSessionEnabled: React.Dispatch<React.SetStateAction<boolean>>
    setIsTimerEnabled: React.Dispatch<React.SetStateAction<boolean>>
    setTimerDuration: React.Dispatch<React.SetStateAction<number>>
    setIsTimerAutoEnabled: React.Dispatch<React.SetStateAction<boolean>>
    setWaitSeconds: React.Dispatch<React.SetStateAction<number>>
    setIsAutoMoveBackToMainSession: React.Dispatch<React.SetStateAction<boolean>>
    setIsSubsessionSelectionEnabled: React.Dispatch<React.SetStateAction<boolean>>
    setRoomOptions: (option: SubsessionOptions) => void
}

type UseSubsessionOptionsReturn = readonly [SubsessionOptionsState, SubsessionOptionsActions]

const useSubsessionOptions = (): UseSubsessionOptionsReturn => {
    const [isAutoJoinSubsession, setIsAutoJoinSubsession] = useState<boolean>(false)
    const [isBackToMainSessionEnabled, setIsBackToMainSessionEnabled] = useState<boolean>(true)
    const [isTimerEnabled, setIsTimerEnabled] = useState<boolean>(false)
    const [timerDuration, setTimerDuration] = useState<number>(30 * 60)
    const [isTimerAutoEnabled, setIsTimerAutoEnabled] = useState<boolean>(false)
    const [waitSeconds, setWaitSeconds] = useState<number>(60)
    const [isAutoMoveBackToMainSession, setIsAutoMoveBackToMainSession] = useState(false)
    const [isSubsessionSelectionEnabled, setIsSubsessionSelectionEnabled] = useState(false)

    const setRoomOptions = useCallback((option: SubsessionOptions) => {
        setIsAutoJoinSubsession(!!option.isAutoJoinSubsession)
        setIsBackToMainSessionEnabled(!!option.isBackToMainSessionEnabled)
        setIsTimerEnabled(!!option.isTimerEnabled)
        setTimerDuration(option.timerDuration || 30 * 60)
        setIsTimerAutoEnabled(!!option.isTimerAutoEnabled)
        setWaitSeconds(option.waitSeconds || 60)
        setIsAutoMoveBackToMainSession(!!option.isAutoMoveBackToMainSession)
        setIsSubsessionSelectionEnabled(!!option.isSubsessionSelectionEnabled)
    }, [])

    return [
        {
            isAutoJoinSubsession,
            isBackToMainSessionEnabled,
            isTimerEnabled,
            timerDuration,
            isTimerAutoEnabled,
            waitSeconds,
            isAutoMoveBackToMainSession,
            isSubsessionSelectionEnabled
        },
        {
            setIsAutoJoinSubsession,
            setIsBackToMainSessionEnabled,
            setIsTimerEnabled,
            setTimerDuration,
            setIsTimerAutoEnabled,
            setWaitSeconds,
            setIsAutoMoveBackToMainSession,
            setIsSubsessionSelectionEnabled,
            setRoomOptions
        }
    ] as const
}

export { useSubsessionOptions }
